import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK — Capacités des types de compte (table `typecompte`)
//
// Le type de compte n'est plus interprété via des listes d'ID écrites
// en dur : chaque ligne de `typecompte` porte une colonne `capacites`
// (produits / services / mixte / marketplace / restauration / client / autre).
// La table devient la source de vérité : un nouveau type créé côté
// SuperAdmin est reconnu automatiquement dès qu'il a des capacités.
//
// Le cache est hydraté une fois au démarrage de l'application, puis relu
// de façon synchrone par `getStoreCapabilities`.
// ─────────────────────────────────────────────────────────────

export type Capacity =
  | 'produits'
  | 'services'
  | 'mixte'
  | 'marketplace'
  | 'restauration'
  | 'client'
  | 'autre';

const cache = new Map<number, Capacity>();
let loaded = false;
let loading: Promise<void> | null = null;

/** Liste des capacités proposées côté SuperAdmin (avec libellés). */
export const CAPACITY_OPTIONS: { value: Capacity; label: string; hint: string }[] = [
  { value: 'produits', label: 'Produits', hint: 'Boutique de produits uniquement' },
  { value: 'services', label: 'Services', hint: 'Prestations de services uniquement' },
  { value: 'mixte', label: 'Mixte', hint: 'Produits ET services' },
  { value: 'marketplace', label: 'Marketplace', hint: 'Place de marché multi-vendeurs' },
  { value: 'restauration', label: 'Restauration', hint: 'Menu + horaires restaurant' },
  { value: 'client', label: 'Client', hint: 'Compte client (pas de boutique)' },
  { value: 'autre', label: 'Autre / adaptatif', hint: 'Adapté automatiquement au contenu' },
];

/** Capacité d'un type de compte, si elle est déjà connue (lecture synchrone). */
export function getCachedCapacity(typecompte?: number | null): Capacity | null {
  const id = Number(typecompte);
  if (!Number.isFinite(id)) return null;
  return cache.get(id) ?? null;
}

/** Charge (une seule fois) les capacités de tous les types de compte. */
export async function loadAccountCapabilities(): Promise<void> {
  if (loaded) return;
  if (loading) return loading;
  loading = (async () => {
    try {
      const { data } = await supabase.from('typecompte').select('id, capacites');
      (data as { id: number; capacites: string | null }[] | null)?.forEach((row) => {
        const id = Number(row.id);
        if (Number.isFinite(id) && row.capacites) cache.set(id, row.capacites as Capacity);
      });
      loaded = true;
    } catch {
      /* best-effort : on retombera sur la détection par libellé */
    }
  })();
  return loading;
}

/** Force le rechargement du cache (après création/modification d'un type). */
export function refreshAccountCapabilities(): void {
  loaded = false;
  loading = null;
  cache.clear();
}

/** Met à jour une entrée du cache sans recharger toute la table. */
export function setCachedCapacity(typecompte: number, capacites: string | null): void {
  const id = Number(typecompte);
  if (!Number.isFinite(id)) return;
  if (capacites) cache.set(id, capacites as Capacity);
  else cache.delete(id);
}